import React, { useState } from "react";
import { useTryOn } from "../contexts/TryOnContext";
import {
  updateTryOnSessionWithUserInfo,
  subscribeTryOnSession,
} from "../utils/firestoreUtils";
import OnScreenKeyboard from "./OnScreenKeyboard";
import "../styles/hologram.css";
import "./Step3UserInfo.css";

/**
 * Step 3A: User Info Collection (replaces old Step3Processing)
 * Collects: Name, Email/Phone, Privacy Consent
 * Then shows confirmation screen
 * Image upload happens in background while user fills form
 */
function Step3UserInfo() {
  const {
    sessionId,
    setCurrentStep,
    setTryOnResults,
    waitingForPhoto,
    setWaitingForPhoto,
    requestReset,
  } = useTryOn();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    contact: "", // Email or phone
    consentGiven: false,
  });

  const [errors, setErrors] = useState({});
  // Track which input the on-screen keyboard should type into.
  // Defaults to 'name' so users can start typing immediately.
  const [focusedField, setFocusedField] = useState("name");

  const appendToFocused = (char) => {
    setFormData((prev) => ({
      ...prev,
      [focusedField]: prev[focusedField] + char,
    }));
  };
  const handleKbKey = (key) => appendToFocused(key);
  const handleKbSpace = () => appendToFocused(" ");
  const handleKbBackspace = () => {
    setFormData((prev) => ({
      ...prev,
      [focusedField]: prev[focusedField].slice(0, -1),
    }));
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    // Name validation
    const nameTrimmed = formData.name.trim();
    if (!nameTrimmed) {
      newErrors.name = "Name is required";
    } else if (nameTrimmed.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    } else if (!/[a-zA-Z]/.test(nameTrimmed)) {
      newErrors.name = "Name must contain at least one letter";
    } else if (!/^[a-zA-Z\s\-'.]+$/.test(nameTrimmed)) {
      newErrors.name =
        "Name can only contain letters, spaces, hyphens, and apostrophes";
    }

    // Contact validation (email or phone)
    const contactTrimmed = formData.contact.trim();
    if (!contactTrimmed) {
      newErrors.contact = "Email or phone is required";
    } else {
      const isEmail = contactTrimmed.includes("@");

      if (isEmail) {
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(contactTrimmed)) {
          newErrors.contact = "Please enter a valid email address";
        } else if (contactTrimmed.includes("..")) {
          newErrors.contact = "Email contains consecutive dots";
        } else if (!contactTrimmed.split("@")[1].includes(".")) {
          newErrors.contact =
            "Email domain must contain a dot (e.g., gmail.com)";
        }
      } else {
        // Phone validation
        const digitsOnly = contactTrimmed.replace(/[\s\-()/]/g, "");

        if (!/^\d+$/.test(digitsOnly)) {
          newErrors.contact =
            "Phone number can only contain digits, spaces, dashes, and parentheses";
        } else if (digitsOnly.length < 10) {
          newErrors.contact = "Phone number must be at least 10 digits";
        } else if (digitsOnly.length > 15) {
          newErrors.contact = "Phone number is too long";
        } else if (/^([0-9])\1+$/.test(digitsOnly)) {
          newErrors.contact =
            "Please enter a valid phone number (not all same digits)";
        }
      }
    }

    // Consent validation
    if (!formData.consentGiven) {
      newErrors.consent = "You must accept the privacy policy to continue";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    console.log("Form submitted:", formData);

    try {
      // The session was already created (and generateTryOn already triggered)
      // in Step2 right after the photo upload finished, so it's likely already
      // running or done by the time the user submits this form. Attach user
      // info to the existing session.
      await updateTryOnSessionWithUserInfo(sessionId, {
        userName: formData.name,
        userContact: formData.contact,
        consentGiven: formData.consentGiven,
      });
      console.log("Session updated with user info");

      // Wait for ALL slots to finish (completed or failed) before advancing so
      // the gallery renders a stable, fully-populated deck instead of items
      // streaming in mid-browse.
      const allSettled = (results) =>
        Array.isArray(results) &&
        results.length > 0 &&
        results.every(
          (r) => r && (r.status === "completed" || r.status === "failed"),
        );

      setWaitingForPhoto(true);
      setIsSubmitting(false);

      const finalResults = await new Promise((resolve, reject) => {
        const unsubscribe = subscribeTryOnSession(sessionId, (data, err) => {
          const resultsArr = Array.isArray(data?.results) ? data.results : [];
          console.log("[Step3] subscription fired:", {
            hasData: !!data,
            status: data?.status,
            resultsCount: resultsArr.length,
            resultStatuses: resultsArr.map((r) => r?.status),
            err,
          });
          if (err) {
            unsubscribe();
            reject(err);
            return;
          }
          if (allSettled(resultsArr)) {
            console.log("[Step3] all slots settled — resolving");
            unsubscribe();
            resolve(resultsArr);
          } else if (data?.status === "failed") {
            unsubscribe();
            reject(new Error("Try-on generation failed"));
          }
        });
      }).catch((tryOnErr) => {
        console.error("Try-on generation failed:", tryOnErr);
        setWaitingForPhoto(false);
        alert("Photo generation failed. Please try again.");
        return null;
      });

      if (!finalResults) return;

      // Filter out failures so the gallery only shows successful try-ons.
      const successful = finalResults.filter(
        (r) => r && r.status === "completed" && r.tryOnImagePublicUrl,
      );

      console.log(
        "[Step3] advancing to Step4 with",
        successful.length,
        "results",
      );
      setTryOnResults(successful);
      setCurrentStep(4);
      // intentionally do NOT setWaitingForPhoto(false) — let unmount handle it
    } catch (error) {
      console.error("Error submitting form:", error);
      setIsSubmitting(false);
      setWaitingForPhoto(false);
      alert("An error occurred. Please try again.");
    }
  };

  const handleGoHome = () => {
    requestReset();
  };

  // Loading screen while we wait for the photo to finish generating.
  // Stays mounted until Step3 itself unmounts, so there's never a frame where
  // the form is visible after the user has already submitted.
  if (waitingForPhoto) {
    return (
      <div className="step3-holo-container">
        <div className="holo-grid-background"></div>
        <div className="processing-overlay">
          <div className="processing-content-holo holo-card">
            <h2 className="processing-title-holo holo-text">
              Finishing up your photos...
            </h2>
            <p className="subtitle-text">This usually takes about 30 seconds</p>

            <button
              type="button"
              className="holo-button-secondary almost-ready-home-btn"
              onClick={handleGoHome}
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form Screen
  return (
    <div className="step3-holo-container">
      {/* Holographic grid background - matches home page */}
      <div className="holo-grid-background"></div>
      <div className="processing-overlay">
        <div className="user-info-content holo-card">
          <h2 className="processing-title-holo holo-text">
            Just a Few Details
          </h2>
          <p className="subtitle-text">
            We'll send you a code when your photos are ready
          </p>

          <form onSubmit={handleSubmit} className="user-info-form">
            {/* Name Input */}
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Name
              </label>
              <input
                type="text"
                id="name"
                className={`form-input ${errors.name ? "error" : ""}`}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                onFocus={() => setFocusedField("name")}
                placeholder="Enter your name"
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            {/* Email/Phone Input */}
            <div className="form-group">
              <label htmlFor="contact" className="form-label">
                Email or Phone
              </label>
              <input
                type="text"
                id="contact"
                className={`form-input ${errors.contact ? "error" : ""}`}
                value={formData.contact}
                onChange={(e) =>
                  setFormData({ ...formData, contact: e.target.value })
                }
                onFocus={() => setFocusedField("contact")}
                placeholder="example@email.com or 1234567890"
              />
              {errors.contact && (
                <span className="error-text">{errors.contact}</span>
              )}
            </div>

            {/* Privacy Consent */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.consentGiven}
                  onChange={(e) =>
                    setFormData({ ...formData, consentGiven: e.target.checked })
                  }
                  className="checkbox-input"
                />
                <span className="checkbox-text">
                  I agree to the{" "}
                  <a href="/privacy" target="_blank" className="privacy-link">
                    Privacy Policy
                  </a>{" "}
                  and consent to receive notifications
                </span>
              </label>
              {errors.consent && (
                <span className="error-text">{errors.consent}</span>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="submit-btn holo-card"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Submit"}
            </button>
          </form>
        </div>

        <OnScreenKeyboard
          onKeyPress={handleKbKey}
          onBackspace={handleKbBackspace}
          onSpace={handleKbSpace}
          uppercase={false}
        />
      </div>
    </div>
  );
}

export default Step3UserInfo;
